import type { MulticallRetryFunction } from '../../../types'
import { buildWithdrawalReader } from './readers'
import type { LstWithdrawalKnownIds } from './readers'
import { getLstWithdrawalRegistry } from './registry'
import type { LstWithdrawalRegistryEntry } from './registry'
import type { LstWithdrawalRequestsByLst } from './types'

/** Toggle to silence per-LST withdrawal-reader warnings. */
const WITHDRAWALS_DEBUG = false
const warn = (...args: unknown[]) => {
  if (WITHDRAWALS_DEBUG) console.warn(...args)
}

/** Optional knobs for the orchestrator. */
export interface LstWithdrawalFetchOptions {
  /** Per-LST caller-supplied IDs. Required for protocols whose
   *  readers are `callerSupplied`-shaped (Mantle, Puffer, TruFin) —
   *  those readers return `[]` when no IDs are supplied. Ignored by
   *  protocols with on-chain enumeration. Keyed by lowercased LST
   *  share-token address. */
  knownIds?: LstWithdrawalKnownIds
  /** Caller-supplied registry entries appended to the static registry.
   *  Lets the orchestrator read withdrawal requests for vaults not in
   *  the hardcoded LST allowlist — e.g. an arbitrary ERC-7540 vault
   *  surfaced by the public-data lookup. Each entry needs at least
   *  `{ lst, brand, symbol, reader }`; the `erc7540` reader needs only
   *  the address. De-duped against the static registry (static wins). */
  extraEntries?: LstWithdrawalRegistryEntry[]
}

/**
 * Fetch a user's pending LST withdrawal requests across all LSTs on
 * a chain.
 *
 * One reader-`fetch` invocation per registered LST, all running in
 * parallel via `Promise.all`. Per-reader failures (RPC error,
 * malformed response, unsupported protocol) are logged and produce
 * an empty array — they don't sink the whole call.
 *
 * Returns a map keyed by lowercased LST share-token address.
 * Empty-array entries are kept so callers can render "no pending
 * requests" UX uniformly.
 *
 * For protocols with no user-keyed on-chain storage and no public
 * subgraph (Mantle mETH, Puffer pufETH, TruFin TruMATIC), pass
 * `options.knownIds[lstAddress]` — the list of withdrawal IDs the
 * caller captured at request time. See `INDEXING_STRATEGIES.md`
 * "Caller-supplied IDs" section.
 *
 * Note: the orchestrator runs N parallel multicalls (one per LST,
 * possibly with internal sub-stages). Per-chain consolidation is not
 * yet implemented — see GAPS.md section F for the optimization
 * trade-off.
 */
export const getLstWithdrawalRequests = async (
  user: string,
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  options?: LstWithdrawalFetchOptions,
): Promise<LstWithdrawalRequestsByLst> => {
  const entries = getLstWithdrawalRegistry(chainId, options?.extraEntries)
  if (entries.length === 0) return {}

  const lcUser = user.toLowerCase()
  const knownIds = options?.knownIds
  const results = await Promise.all(
    entries.map(async (entry) => {
      const reader = buildWithdrawalReader(entry)
      const lstKey = entry.lst.toLowerCase()
      const ids = knownIds?.[lstKey]
      try {
        return await reader.fetch(lcUser, multicallRetry, chainId, entry, ids)
      } catch (e) {
        warn(
          `[lst-withdrawals] ${entry.brand} ${entry.symbol} (${entry.lst}) failed:`,
          (e as { message?: string })?.message ?? e,
        )
        return []
      }
    }),
  )

  const out: LstWithdrawalRequestsByLst = {}
  for (let i = 0; i < entries.length; i++) {
    out[entries[i].lst.toLowerCase()] = results[i]
  }
  return out
}

/**
 * Alias for {@link getLstWithdrawalRequests}. The orchestrator is no
 * longer LST-specific — with `options.extraEntries` it reads withdrawal
 * requests for any vault (e.g. arbitrary ERC-7540) — so this name reads
 * better at non-LST call sites. Same signature, same behaviour.
 */
export const getVaultWithdrawalRequests = getLstWithdrawalRequests
